import { useState } from 'react';
import { CheckIcon, RepeatIcon } from 'lucide-react';
import { toast } from 'sonner';
import { ProductImage } from '../../../ui/ProductImage';
import { useCatalog } from '../../../contexts/CatalogContext';
import { useSignedInAccount } from '../../../contexts/AccountContext';
import { policyCopy } from '../../../data/policies';
import { plural } from '../../../core/format/text';
import { refillableFromOrders } from '../../../utils/account';
import { btnPrimary } from '../../../ui/styles';
import { accountHeading } from '../../../ui/sections';

/**
 * Nog geen (of een gestopt) abonnement: de producten die ze al gebruikt,
 * aan te vinken en in één tik automatisch bij te vullen. Staat op Thuis (#levering).
 */
export function SubscribePrompt() {
  const {
    account,
    subscription
  } = useSignedInAccount();
  const {
    productById
  } = useCatalog();
  const options = refillableFromOrders(account, productById);
  const shown = options;
  // Alleen wat ze ziet staat aangevinkt: nooit een product in het abonnement dat buiten beeld viel.
  const [picked, setPicked] = useState<string[]>(() => shown.map(p => p.id));
  const [busy, setBusy] = useState(false);
  if (!options.length) return null;
  const restarting = account.subscription?.status === 'gestopt';
  const toggle = (id: string) => setPicked(l => l.includes(id) ? l.filter(x => x !== id) : [...l, id]);
  const chosen = picked.filter(id => shown.some(p => p.id === id));
  const start = async () => {
    setBusy(true);
    const ok = await subscription({
      type: 'start',
      productIds: chosen
    });
    setBusy(false);
    if (!ok) return;
    toast.success(restarting ? 'Je abonnement loopt weer' : 'Je Bewust Abonnement staat klaar', {
      description: `${policyCopy.firstDelivery}. Aanpassen kan tot de dag ervoor.`
    });
  };
  return <section aria-labelledby="subscribe-title" className="bg-shell p-5 ring-1 ring-line sm:p-6" data-id="element-6176" data-mp="13t7smknudvff">
      <p className="flex items-center gap-2 text-[13px] text-muted" data-id="element-6177" data-mp="18qzm3j44xplq">
        <RepeatIcon className="h-3.5 w-3.5 text-clay" strokeWidth={1.5} aria-hidden="true" data-id="element-6178" data-mp="10qtjuq9gvm3v" />
        Automatisch bijvullen
      </p>
      <h2 id="subscribe-title" className={`mt-1 ${accountHeading}`} data-id="element-6179" data-mp="wpzwbxzxs9bi">
        {restarting ? 'Weer automatisch laten komen?' : 'Nooit halverwege zonder.'}
      </h2>
      <p className="mt-2 text-[14px] leading-relaxed text-muted" data-id="element-6180" data-mp="1ammw08j9xdl4">
        Altijd zonder verzendkosten. {policyCopy.subscriptionGift}. Opschuiven of pauzeren kan altijd, in één tik.
      </p>

      <ul className="mt-4 space-y-2" aria-label="Welke producten wil je laten bijvullen?" data-id="element-6181" data-mp="1jnb82xp3x3vd">
        {shown.map(p => {
        const on = picked.includes(p.id);
        return <li key={p.id} data-id="element-6182" data-mp="1tepgxw2y7g16">
              <button type="button" role="checkbox" aria-checked={on} onClick={() => toggle(p.id)} className={`flex w-full items-center gap-3 p-2 pr-3 text-left ring-1 transition-shadow duration-150 ease-soft ${on ? 'ring-ink' : 'ring-line hover:ring-ink/40'}`} data-id="element-6183" data-mp="1oge73lcnl5iy">
                <span className="h-10 w-10 shrink-0 bg-sand" data-id="element-6184" data-mp="2i0vim5d8toys">
                  <ProductImage src={p.image} alt="" className="h-full w-full p-0.5" data-id="element-6185" data-mp="2m28lq7oybe20" />
                </span>
                <span className="min-w-0 flex-1 truncate text-[14px] text-ink" data-id="element-6186" data-mp="2814kltph4rty">{p.name}</span>
                <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full ${on ? 'bg-ink text-shell' : 'ring-1 ring-line'}`} aria-hidden="true" data-id="element-6187" data-mp="2d0hhl7a27fa9">
                  {on && <CheckIcon className="h-3 w-3" strokeWidth={2.5} data-id="element-6188" data-mp="232upfj3r1r8c" />}
                </span>
              </button>
            </li>;
      })}
      </ul>
      <div className="mt-5" data-id="element-6189" data-mp="1pdj2oe1l8feg">
        <button type="button" disabled={!chosen.length || busy} onClick={() => void start()} className={btnPrimary} data-id="element-6190" data-mp="1uasshusw2t2t">
          {restarting ? 'Opnieuw starten' : 'Start met bijvullen'}
          {chosen.length > 0 && <span className="text-shell/70" data-id="element-6191" data-mp="1kcbuwezg3g7s">· {plural(chosen.length, 'product', 'producten')}</span>}
        </button>
      </div>
    </section>;
}